import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Trash2, Download } from "lucide-react";

import { adminApi } from "@/lib/api";
import {
  AdminInput,
  AdminSelect,
  AdminTextarea,
  ConfirmDialog,
  EmptyState,
  FieldLabel,
  Modal,
  StatusBadge,
} from "@/components/admin/AdminUI";

const STATUSES = ["new", "contacted", "in_progress", "won", "lost"];
const TYPES = ["Residential Design", "BIM Production", "General Inquiry"];

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", project_type: "", q: "" });
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.project_type) params.project_type = filters.project_type;
    if (filters.q) params.q = filters.q;
    setLoading(true);
    adminApi.listLeads(params).then((d) => {
      setLeads(d);
      setLoading(false);
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const updateStatus = async (id, status) => {
    try {
      const updated = await adminApi.updateLead(id, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? updated : l)));
      if (selected?.id === id) setSelected(updated);
      toast.success("Status updated");
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const addNote = async () => {
    if (!selected || !note.trim()) return;
    try {
      const updated = await adminApi.updateLead(selected.id, { add_note: note });
      setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
      setSelected(updated);
      setNote("");
      toast.success("Note added");
    } catch (e) {
      toast.error("Failed to add note");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await adminApi.deleteLead(confirmDelete.id);
      setLeads((prev) => prev.filter((l) => l.id !== confirmDelete.id));
      setConfirmDelete(null);
      if (selected?.id === confirmDelete.id) setSelected(null);
      toast.success("Lead deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const csv = useMemo(() => {
    if (!leads.length) return "";
    const header = ["created_at", "name", "email", "project_type", "status", "message"].join(",");
    const rows = leads.map((l) =>
      [l.created_at, l.name, l.email, l.project_type, l.status, JSON.stringify(l.message || "")]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(","),
    );
    return [header, ...rows].join("\n");
  }, [leads]);

  const exportCsv = () => {
    if (!csv) return;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `minga-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="body-text">Review and manage inbound project enquiries.</p>
        <button type="button" onClick={exportCsv} className="btn-outline" disabled={!leads.length}>
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="admin-card rounded-sm p-4 md:p-5 grid md:grid-cols-4 gap-3">
        <label className="md:col-span-2">
          <FieldLabel>Search</FieldLabel>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray" />
            <AdminInput
              placeholder="Name, email or message"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              className="pl-9"
            />
          </div>
        </label>
        <label>
          <FieldLabel>Status</FieldLabel>
          <AdminSelect value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replace("_", " ")}
              </option>
            ))}
          </AdminSelect>
        </label>
        <label>
          <FieldLabel>Project type</FieldLabel>
          <AdminSelect value={filters.project_type} onChange={(e) => setFilters((f) => ({ ...f, project_type: e.target.value }))}>
            <option value="">All types</option>
            {TYPES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </AdminSelect>
        </label>
      </div>

      {/* Table */}
      <div className="admin-card rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-10"><EmptyState title="Loading leads\u2026" /></div>
        ) : leads.length === 0 ? (
          <div className="p-6"><EmptyState title="No leads found" description="Contact submissions will appear here." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="text-left border-b border-white/8 bg-white/[0.02] text-[11px] uppercase tracking-[0.14em] text-brand-gray">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    className="border-b border-white/6 hover:bg-white/[0.02] cursor-pointer"
                    onClick={() => {
                      setSelected(l);
                      setNote("");
                    }}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-brand-gray">
                      {new Date(l.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 font-medium">{l.name}</td>
                    <td className="px-4 py-3 text-brand-white/85">{l.email}</td>
                    <td className="px-4 py-3">{l.project_type}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={l.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDelete(l);
                        }}
                        className="text-brand-gray hover:text-brand-red"
                        aria-label="Delete lead"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail modal */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title="Lead Details" wide>
        {selected && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
              <Info label="Name" value={selected.name} />
              <Info label="Email" value={<a href={`mailto:${selected.email}`} className="text-brand-yellow hover:underline">{selected.email}</a>} />
              <Info label="Project Type" value={selected.project_type} />
              <Info label="Source" value={selected.source_page || "—"} />
              <Info label="Received" value={new Date(selected.created_at).toLocaleString()} />
              <Info label="Status" value={<StatusBadge status={selected.status} />} />
            </div>

            <div>
              <FieldLabel>Message</FieldLabel>
              <div className="body-text whitespace-pre-wrap surface p-4 rounded-sm text-sm">
                {selected.message}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <FieldLabel>Change status</FieldLabel>
                <AdminSelect value={selected.status} onChange={(e) => updateStatus(selected.id, e.target.value)}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{s.replace("_", " ")}</option>
                  ))}
                </AdminSelect>
              </div>
              <div>
                <FieldLabel>Add internal note</FieldLabel>
                <div className="flex gap-2">
                  <AdminInput
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Write a note\u2026"
                  />
                  <button type="button" onClick={addNote} className="btn-primary" disabled={!note.trim()}>
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div>
              <FieldLabel>Notes history</FieldLabel>
              {(selected.notes || []).length === 0 ? (
                <p className="body-text text-sm">No internal notes yet.</p>
              ) : (
                <ul className="space-y-3">
                  {selected.notes.map((n) => (
                    <li key={n.id} className="surface p-3 rounded-sm text-sm">
                      <p>{n.content}</p>
                      <p className="text-[11px] text-brand-gray mt-2 uppercase tracking-widest">
                        {n.author} • {new Date(n.created_at).toLocaleString()}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={doDelete}
        title="Delete lead"
        description={`Are you sure you want to delete this lead from ${confirmDelete?.name}? This action cannot be undone.`}
      />
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="eyebrow-muted mb-1">{label}</p>
      <div className="text-sm text-brand-white">{value}</div>
    </div>
  );
}
